import type { RoleMismatch } from "@repo/shared/hooks";
import { Trans, useTranslation } from "@repo/shared/i18n";
import type { PlayerRole } from "@repo/shared/redux";
import { AlertTriangle } from "lucide-react";

/**
 * Same-role nudge shown above the sections: under "Best Role" with mixed
 * own-best roles the columns aren't comparable, so suggest grading everyone
 * under one shared role (a subtle link — clicking selects it), or just warn
 * when the players share no positions. Renders nothing without a mismatch.
 */
export const CompareRoleNudge = ({
  roleMismatch,
  onSelectRole,
}: {
  roleMismatch?: RoleMismatch<PlayerRole>;
  onSelectRole: (role: PlayerRole) => void;
}) => {
  const { t } = useTranslation("compare");
  if (!roleMismatch) return null;
  const { sharedRoles, suggestedRole } = roleMismatch;
  return (
    <div className="flex items-center gap-x-2 text-xs text-neutral-400">
      <AlertTriangle className="size-4 shrink-0 text-amber-500" aria-hidden />
      <span>
        <span>
          {sharedRoles ? t("nudge.differentRoles") : t("nudge.noOverlap")}
        </span>
        {suggestedRole && (
          <>
            {" "}
            <Trans
              ns="compare"
              i18nKey="nudge.suggestion"
              values={{
                role: `${suggestedRole.name} ${suggestedRole.position}`,
              }}
              components={{
                role: (
                  <button
                    type="button"
                    className="cursor-pointer text-neutral-200 underline underline-offset-2 hover:text-white"
                    onClick={() => onSelectRole(suggestedRole)}
                  />
                ),
              }}
            />
          </>
        )}
      </span>
    </div>
  );
};
